import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Avatar,
  Grid,
  Card,
  CardContent,
  AppBar,
  Toolbar,
  IconButton,
  Divider,
  MenuItem,
  LinearProgress,
  Chip,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack,
  Logout,
  School,
  CloudUpload,
  AttachFile,
  Delete,
  InsertDriveFile,
  PictureAsPdf,
  Image as ImageIcon,
  Send,
  Person,
  Assignment,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { createRequest } from "../services/dataServiceWrapper";
import { sendApproverNotification } from '../services/emailService';
import { uploadFiles } from '../services/fileService';

// Change types with their descriptions
const changeTypes = [
  { id: 1, name: 'Facilitation Date/Time', group: 'operational' },
  { id: 2, name: 'Facilitator Change', group: 'operational' },
  { id: 3, name: 'Delivery Mode', group: 'operational' },
  { id: 4, name: 'Facilitation Cancellation', group: 'operational' },
  { id: 5, name: 'Marker Change', group: 'operational' },
  { id: 6, name: 'Marker or Facilitator missing from the MUL', group: 'operational' },
  { id: 7, name: 'Assessment Change', group: 'academic' },
  { id: 8, name: 'Academic Content', group: 'academic' },
];

// Validation schema
const schema = yup.object({
  name: yup.string().required('Name is required'),
  changeType: yup.number().required('Change type is required').min(1).max(8),
  unitCode: yup.string().required('Unit code and name is required'),
  reason: yup.string().required('Reason is required'),
  requestedAction: yup.string().required('Requested action is required'),
});

const AcademicChanges = () => {
  const { accounts, instance } = useMsal();
  const navigate = useNavigate();
  const account = accounts[0];
  const [submitting, setSubmitting] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: account?.name || '',
      changeType: '',
      unitCode: '',
      reason: '',
      requestedAction: '',
    },
  });

  const selectedChangeType = watch('changeType');

  // Get approvers based on change type
  const getApprovers = (changeTypeId) => {
    const changeTypeNum = parseInt(changeTypeId);
    
    if (changeTypeNum >= 1 && changeTypeNum <= 6) {
      // Types 1-6: Peter Laughlin, Angela Sawyer, and Stephen Parker
      return [
        { name: 'Peter Laughlin', email: 'plaughlin@acom.edu.au' },
        { name: 'Angela Sawyer', email: 'asawyer@acom.edu.au' },
        { name: 'Stephen Parker', email: 'sparker@acom.edu.au' },
      ];
    } else if (changeTypeNum === 7 || changeTypeNum === 8) {
      // Types 7-8: Stephen Parker only
      return [
        { name: 'Stephen Parker', email: 'sparker@acom.edu.au' },
      ];
    }
    return [];
  };

  // Get secondary recipients based on change type (sent after approval)
  const getSecondaryRecipients = (changeTypeId) => {
    const changeTypeNum = parseInt(changeTypeId);
    
    if (changeTypeNum >= 1 && changeTypeNum <= 6) {
      // Types 1-6: moodle@, registrar@, and info@
      return [
        'moodle@acom.edu.au',
        'registrar@acom.edu.au',
        'info@acom.edu.au',
      ];
    } else if (changeTypeNum === 7 || changeTypeNum === 8) {
      // Types 7-8: moodle@ and info@
      return [
        'moodle@acom.edu.au',
        'info@acom.edu.au',
      ];
    }
    return [];
  };

  // File upload handling
  const onDrop = useCallback((acceptedFiles) => {
    setUploadProgress(20);
    const interval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 20;
      });
    }, 200);

    const newDocuments = acceptedFiles.map(file => ({
      id: Date.now() + Math.random(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
    }));
    
    setTimeout(() => {
      setDocuments([...documents, ...newDocuments]);
      setUploadProgress(0);
    }, 1200);
  }, [documents]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/*': ['.png', '.jpg', '.jpeg'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    maxSize: 10485760, // 10MB
  });

  const removeDocument = (id) => {
    setDocuments(documents.filter(doc => doc.id !== id));
  };

  const getFileIcon = (type) => {
    if (type.includes('pdf')) return <PictureAsPdf color="error" />;
    if (type.includes('image')) return <ImageIcon color="primary" />;
    return <InsertDriveFile color="action" />;
  };

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const changeType = changeTypes.find(ct => ct.id === parseInt(data.changeType));
      const approvers = getApprovers(data.changeType);
      const secondaryRecipients = getSecondaryRecipients(data.changeType);
      
      // Create the request
      const newRequest = await createRequest({
        type: 'academic',
        title: `Academic Change - ${changeType.name} - ${data.unitCode}`,
        name: data.name, // Explicitly include the name field
        unitCode: data.unitCode,
        changeType: data.changeType,
        reason: data.reason,
        requestedAction: data.requestedAction,
        changeTypeName: changeType.name,
        changeTypeGroup: changeType.group,
        submittedBy: account?.email || account?.username || 'current.user@acom.edu.au',
        documents: documents.map(doc => ({ name: doc.name, size: doc.size })),
        notes: [],
        status: 'pending',
        approvers: approvers.map(a => a.email),
        approverNames: approvers.map(a => a.name),
        secondaryRecipients: secondaryRecipients,
        requiresApprovalFrom: 'any', // For types 1-6, any approver can approve
        approvedBy: null,
        approvalDate: null,
      });

      // Upload files if any
      if (documents.length > 0) {
        try {
          const filesToUpload = documents.map(doc => doc.file).filter(file => file);
          if (filesToUpload.length > 0) {
            await uploadFiles(newRequest.id, filesToUpload, account?.email || 'unknown');
          }
        } catch (uploadError) {
          console.error('File upload failed:', uploadError);
          // Continue anyway - request is created
        }
      }

      // Send email notifications to all approvers
      for (const approver of approvers) {
        try {
          await sendApproverNotification(newRequest, approver.email);
        } catch (emailError) {
          console.error('Email notification failed:', emailError);
        }
      }

      toast.success('Academic change request submitted successfully!');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error submitting request:', error);
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    instance.logoutPopup({
      postLogoutRedirectUri: '/',
    });
    toast.success('Logged out successfully');
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#f5f5f5' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#0078d4' }}>
        <Toolbar>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <img
              src="/acom-logo.png"
              alt="ACOM Logo"
              style={{ 
                height: '40px', 
                marginRight: '16px',
                objectFit: 'contain',
                borderRadius: '8px',
                padding: '4px',
                backgroundColor: 'white'
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              Apps Portal
            </Typography>
          </Box>

          <Button
            color="inherit"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                {account?.name || 'User'}
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>
                {account?.username || account?.email || 'user@acom.edu.au'}
              </Typography>
            </Box>
            <Avatar sx={{ bgcolor: '#ff6b00' }}>
              {account?.name?.charAt(0) || 'U'}
            </Avatar>
            <IconButton color="inherit" onClick={handleLogout}>
              <Logout />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="lg" sx={{ py: 4, flexGrow: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <form onSubmit={handleSubmit(onSubmit)}>
            <Paper sx={{ p: 4 }}>
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, display: 'flex', alignItems: 'center' }}>
                <School sx={{ mr: 2, fontSize: 32 }} />
                Academic Changes Request
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                Submit a request for academic changes to units or courses
              </Typography>

              <Divider sx={{ mb: 4 }} />


              {/* Requestor Information Section */}
              <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                    <Person sx={{ mr: 1 }} />
                    Requestor Information
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Name"
                        {...register('name')}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        InputLabelProps={{ shrink: true }}
                        placeholder="Enter your full name"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Unit Code and Name"
                        {...register('unitCode')}
                        error={!!errors.unitCode}
                        helperText={errors.unitCode?.message}
                        InputLabelProps={{ shrink: true }}
                        placeholder="e.g., THEO101 - Introduction to Theology"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              {/* Change Type Section */}
              <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                    <Assignment sx={{ mr: 1 }} />
                    Change Type Selection
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    label="Select Change Type"
                    {...register('changeType')}
                    error={!!errors.changeType}
                    helperText={errors.changeType?.message}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem value="">Select a change type</MenuItem>
                    {changeTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.id}. {type.name}
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* Show approvers based on selected change type */}
                  {selectedChangeType && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Approvers:</strong> {getApprovers(selectedChangeType).map(a => a.name).join(', ')}
                      </Typography>
                      {parseInt(selectedChangeType) >= 1 && parseInt(selectedChangeType) <= 6 && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          After approval, notifications will be sent to: moodle@acom.edu.au, registrar@acom.edu.au, info@acom.edu.au
                        </Typography>
                      )}
                      {(parseInt(selectedChangeType) === 7 || parseInt(selectedChangeType) === 8) && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          After approval, notifications will be sent to: moodle@acom.edu.au, info@acom.edu.au
                        </Typography>
                      )}
                    </Alert>
                  )}
                </CardContent>
              </Card>

              {/* Reason for Change Section */}
              <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Reason for Change
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={5}
                    {...register('reason')}
                    error={!!errors.reason}
                    helperText={errors.reason?.message}
                    InputLabelProps={{ shrink: true }}
                    placeholder="Please provide a detailed explanation of why this change is necessary..."
                  />
                </CardContent>
              </Card>

              {/* Requested Action Section */}
              <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Requested Action
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    {...register('requestedAction')}
                    error={!!errors.requestedAction}
                    helperText={errors.requestedAction?.message}
                    InputLabelProps={{ shrink: true }}
                    placeholder="Please describe the specific actions you want taken..."
                  />
                </CardContent>
              </Card>

              {/* File Upload Section */}
              <Card sx={{ mb: 3, bgcolor: '#f8f9fa' }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
                    <CloudUpload sx={{ mr: 1 }} />
                    Supporting Documents
                  </Typography>
                  
                  <Box
                    {...getRootProps()}
                    sx={{
                      border: '2px dashed',
                      borderColor: isDragActive ? 'primary.main' : '#ccc',
                      borderRadius: 2,
                      p: 3,
                      textAlign: 'center',
                      cursor: 'pointer',
                      bgcolor: isDragActive ? 'action.hover' : 'transparent',
                      transition: 'all 0.3s',
                      '&:hover': { 
                        bgcolor: 'action.hover',
                        borderColor: 'primary.main',
                      },
                    }}
                  >
                    <input {...getInputProps()} />
                    <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" gutterBottom>
                      {isDragActive ? 'Drop files here' : 'Drag & drop files here, or click to browse'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Supported formats: PDF, Word documents, Images (max 10MB)
                    </Typography>
                  </Box>

                  {uploadProgress > 0 && uploadProgress < 100 && (
                    <Box sx={{ mt: 2 }}>
                      <LinearProgress variant="determinate" value={uploadProgress} />
                      <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                        Uploading... {uploadProgress}%
                      </Typography>
                    </Box>
                  )}

                  {documents.length > 0 && (
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 2 }}>
                        Uploaded Files ({documents.length})
                      </Typography>
                      {documents.map((doc) => (
                        <Chip
                          key={doc.id}
                          icon={getFileIcon(doc.type)}
                          label={`${doc.name} (${(doc.size / 1024 / 1024).toFixed(2)}MB)`}
                          onDelete={() => removeDocument(doc.id)}
                          sx={{ m: 0.5 }}
                        />
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Submit Button */}
              <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={submitting}
                  sx={{ minWidth: 200 }}
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </Box>
            </Paper>
          </form>
        </motion.div>
      </Container>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 3,
          px: 2,
          mt: 'auto',
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary" align="center">
            © 2025 ACOM. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default AcademicChanges;